import React, {useState, useCallback, useRef} from "react";
import {Field, useForm} from "react-final-form";
import {
    UserExamScoreField,
    UserExamScoreSelectedValue,
} from "../../../../../containers/RegistrationLoginPage/types";
import {ExamScoreDiv, ExamScoreId, ExamScoreInput} from "../../Styled";
import TextField from "../../../../HTMLEntities/Textfield/index";
import CheckBox from "../../../../HTMLEntities/CheckBox/index";
import {
    composeValidators,
    regRequiredValidate,
    numberValidation,
    validateExamScoreField,
} from "../../../../../containers/RegistrationLoginPage/registrationValidation";
import {errorHandler} from "../../../../../containers/RegistrationLoginPage/errorUtils";

interface ExamScoreFieldInterface {
    examScoreValue: UserExamScoreField;
    editable: boolean;
    fieldName: string;
    index: number;
    selectedValue: UserExamScoreSelectedValue[] | null;
    isMobilePopup?: boolean;
}

const ExamScoreField: React.FC<ExamScoreFieldInterface> = ({
    examScoreValue,
    editable,
    fieldName,
    selectedValue,
    index,
    isMobilePopup = false,
}: ExamScoreFieldInterface) => {
    const {id, max, min, name, step} = examScoreValue;

    let examScore: number | null = null;
    selectedValue &&
        selectedValue.map((value) => {
            if (value.id === id) examScore = value.score;
        });

    const [scoreEnable, setScoreEnable] = useState(
        examScore !== undefined && examScore !== null ? true : false,
    );

    const form = useForm();
    const onChangeCallback = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const checkboxStatus = event.target;
            setScoreEnable(checkboxStatus.checked);
            form.resetFieldState(`${fieldName}[${index}][score]`);
            form.change(`${fieldName}[${index}][score]`, undefined);
        },
        [form, fieldName, index],
    );

    const inputFieldRef = useRef<HTMLInputElement | null>(null);

    const examScoreInputClick = useCallback(() => {
        setScoreEnable(true);
        form.change(`${fieldName}[${index}][enabled]`, true);
        setTimeout(() => {
            inputFieldRef.current?.focus();
        }, 50);
    }, [form, fieldName, index]);

    return (
        <ExamScoreDiv isMobilePopup={isMobilePopup}>
            <Field
                name={`${fieldName}[${index}][enabled]`}
                type="checkbox"
                key={`checkbox_${index}_${scoreEnable ? "true" : "false"}`}
                validate={validateExamScoreField}
                {...(scoreEnable ? {initialValue: true} : {initialValue: false})}
                validateFields={[`${fieldName}[${index}][score]`]}
            >
                {(props) => {
                    const onChange = props.input.onChange;
                    props.input.onChange = (change) => {
                        onChangeCallback(change);
                        onChange(change);
                    };
                    return (
                        <CheckBox
                            label={name}
                            id={`examScoreCheckBox_${id}`}
                            inputRFF={props?.input}
                            isDisabled={!editable}
                            error={errorHandler(props?.meta)}
                            isFocused={props?.meta?.active ?? false}
                            isError={props?.meta?.error && props?.meta?.touched}
                            CheckBoxStyle={{
                                mobile: {top: "2px"},
                                laptop: {top: "2px"},
                            }}
                        />
                    );
                }}
            </Field>

            <Field name={`${fieldName}[${index}][id]`} type="number" defaultValue={id}>
                {() => {
                    return <ExamScoreId />;
                }}
            </Field>

            <Field<number>
                name={`${fieldName}[${index}][score]`}
                key={`${fieldName}_${index}_${scoreEnable ? "true" : "false"}`}
                type="number"
                validate={composeValidators(
                    regRequiredValidate("Please mention exam score", scoreEnable),
                    numberValidation(min, max, step, "Invalid score"),
                )}
                validateFields={[]}
                {...(examScore !== undefined && examScore !== null
                    ? {initialValue: examScore}
                    : {})}
            >
                {(props) => {
                    return (
                        <ExamScoreInput onClick={examScoreInputClick} isMobilePopup={isMobilePopup}>
                            <TextField
                                placeholder="Score"
                                type="number"
                                inputRFF={props.input}
                                min={min ?? 0}
                                max={max ?? 100}
                                step={step ?? 1}
                                isFocused={props?.meta?.active ?? false}
                                isFilled={
                                    scoreEnable ? true : props?.input?.value.toString() !== ""
                                }
                                error={errorHandler(props?.meta)}
                                isTouched={props?.meta?.touched}
                                isDisabled={!scoreEnable}
                                scoreWidth={true}
                                ref={inputFieldRef}
                            />
                        </ExamScoreInput>
                    );
                }}
            </Field>
        </ExamScoreDiv>
    );
};

export default ExamScoreField;
